import { z } from 'zod';

import { SMALL_STRING_LIMIT } from './constants';

export type JSONValue =
  | string
  | number
  | boolean
  | null
  | JSONValue[]
  | { [key: string]: JSONValue };

export type JSONObject = Record<string, JSONValue>;
export type JSONArray = Array<JSONValue>;

export const zJSON: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(zJSON),
    z.record(zJSON),
  ])
);

export const zJSONArray: z.ZodType<Array<JSONValue>> = z.lazy(() =>
  z.array(zJSON)
);

export const zJSONObject: z.ZodType<Record<string, JSONValue>> = z.lazy(() =>
  z.record(zJSON)
);

export const jsonValueIsSmall = (value: JSONValue): boolean => {
  switch (typeof value) {
    case 'number':
    case 'boolean':
      return true;
    case 'string':
      return value.length < SMALL_STRING_LIMIT;
    case 'object': {
      if (value === null) return true;
      if (Array.isArray(value))
        return (
          value.length === 0 ||
          (value.length === 1 && jsonValueIsSmall(value[0]!))
        );
      const keys = Object.keys(value);
      return (
        keys.length === 0 ||
        (keys.length === 1 && jsonValueIsSmall(value[keys[0]!]!))
      );
    }
  }
};

export const jsonValueIsEmpty = (value: JSONValue): boolean => {
  switch (typeof value) {
    case 'number':
      return value === 0;
    case 'string':
      return value.length === 0;
    case 'object': {
      if (value === null) return true;
      if (Array.isArray(value))
        return value.length === 0 || value.every(jsonValueIsEmpty);
      const keys = Object.keys(value);
      return (
        keys.length === 0 || keys.every((k) => jsonValueIsEmpty(value[k]!))
      );
    }
    default:
      return false;
  }
};

const STRING_PREVIEW_MAX_STRING_LENGTH = 64;

export const jsonValuePreview = (value: JSONValue): string => {
  switch (typeof value) {
    case 'string':
      return value === ''
        ? 'Empty string'
        : value.length < STRING_PREVIEW_MAX_STRING_LENGTH
        ? value
        : value.slice(0, STRING_PREVIEW_MAX_STRING_LENGTH) + '...';
    case 'number':
      return value.toString();
    case 'boolean':
      return value.toString();
    case 'object': {
      if (value === null) return 'null';
      if (Array.isArray(value)) return value.length === 0 ? '[]' : '[...]';
      const keys = Object.keys(value);
      return keys.length === 0 ? '{}' : '{...}';
    }
  }
};
